"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar, Save } from "lucide-react";
import { useGetSilaAnimal } from "@/features/Animals/animalApiSlice.ts";
import { useParams } from "react-router-dom";
import { SuspenseFallback } from "@/lib/Utils.Components.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { formSchema, FormValues } from "@/models/Sila.Animals.schema";
import { VideoItem } from "@/components/video-input-manager.tsx";
import { AnimalMediaInfo } from "@/features/Animals/Animal-Media-Info.tsx";
import { apiClient } from "@/api/api.ts";
import { GreatGrandparentalInfoForm } from "@/features/Animals/greate-grandparental-info-form.tsx";
import { ParentalInfoForm } from "@/features/Animals/Parental-info-form.tsx";
import { GrandParentalInfoForm } from "@/features/Animals/grandparental-info-form.tsx";
import { MilkProperties } from "./Milk-Properties";
import SequentialUploadNotifier from "@/components/ui/sequential-upload-notifier";

// Mock current data for demonstration
interface FormFieldWrapperProps {
  children: React.ReactNode;
  currentValue: any;
  newValue: any;
  fieldName: string;
}

function FormFieldWrapper({
  children,
  currentValue,
  newValue,
}: FormFieldWrapperProps) {
  const hasChanged =
    currentValue !== newValue && newValue !== undefined && newValue !== "";

  return (
    <div className="space-y-2">
      {children}
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          Current:{" "}
          <span className="font-medium">{currentValue || "not provided"}</span>
        </span>
        {hasChanged && (
          <Badge variant="secondary" className="animate-pulse">
            Changed
          </Badge>
        )}
      </div>
    </div>
  );
}

// Helper to sanitize input value for <Input />
function sanitizeInputValue(
  value: any,
  type: string = "text",
): string | number | undefined {
  if (type === "number") {
    if (value === undefined || value === null || Number.isNaN(value)) return "";
    return value;
  }
  return value ?? "";
}

export default function AnimalProfileForm() {
  const { id } = useParams<{ id: string }>();
  const { data: currentData, isLoading, error } = useGetSilaAnimal(id);
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationKey: ["adminAnimalRegister", id],
    mutationFn: async (data: FormValues) => {
      try {
        const response = await apiClient.patch(
          `/admin/sila-Animals/animal/${id}`,
          data,
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["silaAdminAnimals"] });
      queryClient.invalidateQueries({ queryKey: ["SilaAnimal", id] });
    },
    onError: (error: Error) => {
      if (error instanceof AxiosError) {
        toast.error(`${error.response?.data.message}`);
      } else {
        toast.error("Failed to update animal.");
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["SilaAnimal", id] });
      queryClient.invalidateQueries({ queryKey: ["silaAdminAnimals"] });
    },
  });

  const [activeTab, setActiveTab] = useState("basic");

  // Define the type for the ref to include startUploads
  type UploadRefType = {
    startUploads: () => Promise<{
      animalImageUrls: string[];
      lineageImageUrls: Record<string, string>;
    }>;
  };
  const uploadRef = useRef<UploadRefType>(null);

  // Loading and error handling (do not return early, render only once)
  let content: React.ReactNode = null;
  if (isLoading) {
    content = <SuspenseFallback />;
  } else if (error) {
    content = <div className="text-red-500">Error loading animal data</div>;
  } else if (!currentData) {
    content = <div>No data found</div>;
  }
  const [img, setImg] = useState<File[]>([]);
  const [videos, setVideos] = useState<VideoItem[]>([]);

  useEffect(() => {
    if (currentData) {
      setVideos(currentData.videos ?? []);
    }
  }, [currentData]);

  const [damImageFile, setDamImageFile] = useState<File | string | undefined>();
  const [sireImageFile, setSireImageFile] = useState<
    File | string | undefined
  >();

  // New states for grandparents
  const [paternalGrandfatherImageFile, setPaternalGrandfatherImageFile] =
    useState<File>();
  const [paternalGrandmotherImageFile, setPaternalGrandmotherImageFile] =
    useState<File>();
  const [maternalGrandfatherImageFile, setMaternalGrandfatherImageFile] =
    useState<File>();
  const [maternalGrandmotherImageFile, setMaternalGrandmotherImageFile] =
    useState<File>();

  // Great-grandparent image states (8 total)
  const [
    paternalGrandSireGrandfatherImageFile,
    setPaternalGrandSireGrandfatherImageFile,
  ] = useState<File>();
  const [
    paternalGrandSireGrandmotherImageFile,
    setPaternalGrandSireGrandmotherImageFile,
  ] = useState<File>();
  const [
    paternalGrandDamGrandfatherImageFile,
    setPaternalGrandDamGrandfatherImageFile,
  ] = useState<File>();
  const [
    paternalGrandDamGrandmotherImageFile,
    setPaternalGrandDamGrandmotherImageFile,
  ] = useState<File>();
  const [
    maternalGrandSireGrandfatherImageFile,
    setMaternalGrandSireGrandfatherImageFile,
  ] = useState<File>();
  const [
    maternalGrandSireGrandmotherImageFile,
    setMaternalGrandSireGrandmotherImageFile,
  ] = useState<File>();
  const [
    maternalGrandDamGrandfatherImageFile,
    setMaternalGrandDamGrandfatherImageFile,
  ] = useState<File>();
  const [
    maternalGrandDamGrandmotherImageFile,
    setMaternalGrandDamGrandmotherImageFile,
  ] = useState<File>();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: currentData,
    values: currentData,
    resetOptions: { keepDirtyValues: true },
  });

  const watchedValues = form.watch();

  const formData = form.getValues();

  const lineageUploads = [
    {
      file: damImageFile,
      type: "dam",
      field: "damImageUrl",
      dgr: formData.damDGR,
      displayName: "Dam",
    },
    {
      file: sireImageFile,
      type: "sire",
      field: "sireImageUrl",
      dgr: formData.sireDGR,
      displayName: "Sire",
    },
    {
      file: paternalGrandfatherImageFile,
      type: "paternalGrandfather",
      field: "paternalGrandSireImageUrl",
      dgr: formData.paternalGrandSireDGR,
      displayName: "Paternal Grand Sire",
    },
    {
      file: paternalGrandmotherImageFile,
      type: "paternalGrandmother",
      field: "paternalGrandDamImageUrl",
      dgr: formData.paternalGrandDamDGR,
      displayName: "Paternal Grand Dam",
    },
    {
      file: maternalGrandfatherImageFile,
      type: "maternalGrandfather",
      field: "maternalGrandSireImageUrl",
      dgr: formData.maternalGrandSireDGR,
      displayName: "Maternal Grand Sire",
    },
    {
      file: maternalGrandmotherImageFile,
      type: "maternalGrandmother",
      field: "maternalGrandDamImageUrl",
      dgr: formData.maternalGrandDamDGR,
      displayName: "Maternal Grand Dam",
    },
    // 8 great-grandparents
    {
      file: paternalGrandSireGrandfatherImageFile,
      type: "paternalGrandSireGrandfather",
      field: "paternalGrandSireGrandSireImageUrl",
      dgr: formData.paternalGrandSireGrandSireDGR,
      displayName: "Paternal Grand Sire's Sire",
    },
    {
      file: paternalGrandSireGrandmotherImageFile,
      type: "paternalGrandSireGrandmother",
      field: "paternalGrandSireGrandDamImageUrl",
      dgr: formData.paternalGrandSireGrandDamDGR,
      displayName: "Paternal Grand Sire's Dam",
    },
    {
      file: paternalGrandDamGrandfatherImageFile,
      type: "paternalGrandDamGrandfather",
      field: "paternalGrandDamGrandSireImageUrl",
      dgr: formData.paternalGrandDamGrandSireDGR,
      displayName: "Paternal Grand Dam's Sire",
    },
    {
      file: paternalGrandDamGrandmotherImageFile,
      type: "paternalGrandDamGrandmother",
      field: "paternalGrandDamGrandDamImageUrl",
      dgr: formData.paternalGrandDamGrandDamDGR,
      displayName: "Paternal Grand Dam's Dam",
    },
    {
      file: maternalGrandSireGrandfatherImageFile,
      type: "maternalGrandSireGrandfather",
      field: "maternalGrandSireGrandSireImageUrl",
      dgr: formData.maternalGrandSireGrandSireDGR,
      displayName: "Maternal Grand Sire's Sire",
    },
    {
      file: maternalGrandSireGrandmotherImageFile,
      type: "maternalGrandSireGrandmother",
      field: "maternalGrandSireGrandDamImageUrl",
      dgr: formData.maternalGrandSireGrandDamDGR,
      displayName: "Maternal Grand Sire's Dam",
    },
    {
      file: maternalGrandDamGrandfatherImageFile,
      type: "maternalGrandDamGrandfather",
      field: "maternalGrandDamGrandSireImageUrl",
      dgr: formData.maternalGrandDamGrandSireDGR,
      displayName: "Maternal Grand Dam's Sire",
    },
    {
      file: maternalGrandDamGrandmotherImageFile,
      type: "maternalGrandDamGrandmother",
      field: "maternalGrandDamGrandDamImageUrl",
      dgr: formData.maternalGrandDamGrandDamDGR,
      displayName: "Maternal Grand Dam's Dam",
    },
  ];

  const onSubmitWithLineageUploads: SubmitHandler<FormValues> = async () => {
    try {
      const formData = form.getValues();

      if (!uploadRef.current) {
        toast.error("Upload manager is not ready.");
        return;
      }
      const { animalImageUrls, lineageImageUrls } =
        await uploadRef.current.startUploads();

      // Merge URLs into formData
      const newFormData: Partial<FormValues> = {
        ...formData,
        ...lineageImageUrls,
      };

      if (animalImageUrls.length > 0) {
        newFormData.imageUrls = [...(formData.imageUrls ?? []), ...animalImageUrls];
      }

      newFormData.videos = videos;

      // Submit with new formData
      const update = mutateAsync(newFormData as FormValues);
      toast.promise(update, {
        loading: "Updating Sila Owned Animal Data",
        success: "Animal Data Updated Successfully",
        error: "Failed to update animal data.",
        position: "top-right",
      });
      await update;
      form.reset(newFormData as FormValues);
      setImg([]);
      [setDamImageFile, setSireImageFile, setPaternalGrandfatherImageFile,
        setPaternalGrandmotherImageFile, setMaternalGrandfatherImageFile,
        setMaternalGrandmotherImageFile, setPaternalGrandSireGrandfatherImageFile,
        setPaternalGrandSireGrandmotherImageFile, setPaternalGrandDamGrandfatherImageFile,
        setPaternalGrandDamGrandmotherImageFile, setMaternalGrandSireGrandfatherImageFile,
        setMaternalGrandSireGrandmotherImageFile, setMaternalGrandDamGrandfatherImageFile,
        setMaternalGrandDamGrandmotherImageFile].forEach(clearFile => clearFile(undefined));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update animal data.");
    }
  };

  // async function onSubmit() {
  //   try {
  //     if (!img || img.length === 0) {
  //       toast.error("No image provided.");
  //       return;
  //     }
  //
  //     // 1. Upload all images and collect URLs
  //     const uploadPromises = img.map((image) => {
  //       const storageRef = ref(
  //         storage,
  //         `images/silaAnimals/${Date.now()}-${image.name}`,
  //       );
  //       const uploadTask = uploadBytesResumable(storageRef, image, metadata);
  //
  //       return new Promise<string>((resolve, reject) => {
  //         uploadTask.on(
  //           "state_changed",
  //           (snapshot) => {
  //             // Progress handling (optional)
  //             const progress =
  //               (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
  //             if (snapshot.state === "running") {
  //               toast.info("Images Being Uploaded: " + progress);
  //             }
  //           },
  //           (error) => reject(error),
  //           async () => {
  //             const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
  //             resolve(downloadURL);
  //           },
  //         );
  //       });
  //     });
  //
  //     // 2. Wait for all uploads to finish
  //     const urls = await Promise.all(uploadPromises);
  //
  //     setImgUrls(urls); // if you still need to store them in state
  //
  //     // 3. Register animal ONCE with all image URLs
  //     const formData = form.getValues();
  //     console.log("Form Data:", formData);
  //     toast.promise(
  //       mutateAsync({
  //         ...formData,
  //         imageUrls: urls,
  //         videos: [...videos],
  //       }),
  //       {
  //         success: "Animal Registered Successfully",
  //         position: "top-right",
  //       },
  //     );
  //   } catch (e) {
  //     throw e;
  //   }
  // }

  function safeGet(obj: any, key: string) {
    return obj && typeof obj === "object" && key in obj ? obj[key] : undefined;
  }

  // Convert image URLs to File objects and set them in img state
  // useEffect(() => {
  // 	async function fetchAndSetImages() {
  // 		if (currentData?.imageUrls && currentData.imageUrls.length > 0) {
  // 			const files: File[] = await Promise.all(
  // 				currentData.imageUrls.map(async (url: string, idx: number) => {
  // 					const response = await fetch(url)
  // 					const blob = await response.blob()
  // 					// Try to preserve the extension if possible
  // 					const ext = url.split(".").pop()?.split("?")[0] || "jpg"
  // 					return new File([blob], `animal-image-${idx}.${ext}`, {
  // 						type: blob.type,
  // 					})
  // 				})
  // 			)
  // 			setImg(files)
  // 		}
  // 	}
  //
  // 	fetchAndSetImages()
  // 	// Only run when currentData.imageUrls changes
  // }, [currentData?.imageUrls])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <SequentialUploadNotifier
        ref={uploadRef}
        animalImages={img}
        lineageImages={lineageUploads}
      />
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 text-center text-[#008c8fff]">
          <h1 className="text-4xl font-bold mb-2">Animal Profile Update</h1>
          <p className="text-[#015354ff]">
            Update comprehensive animal information and tracking data
          </p>
        </div>
        {content ? (
          content
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmitWithLineageUploads)}
              className="space-y-6"
            >
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <TabsList
                  className={`grid w-full mb-6 ${watchedValues.bullType === "Dairy Bull" ? "grid-cols-6 text-[#015354ff]" : "grid-cols-8 text-[#015354ff]"}`}
                >
                  <TabsTrigger value="basic" className="text-xs">
                    Basic Info
                  </TabsTrigger>
                  <TabsTrigger value="physical" className="text-xs">
                    Physical
                  </TabsTrigger>
                  <TabsTrigger value="genetic" className="text-xs">
                    Genetic
                  </TabsTrigger>
                  <TabsTrigger value="parental" className="text-xs">
                    Parental
                  </TabsTrigger>
                  <TabsTrigger value="grandparental" className="text-xs">
                    Grand Parental
                  </TabsTrigger>
                  <TabsTrigger value="greatgrandparental" className="text-xs">
                    Great Grand Parental
                  </TabsTrigger>
                  {watchedValues.bullType === "Dairy Bull" && (
                    <TabsTrigger value="milk" className="text-xs">
                      Milk Property
                    </TabsTrigger>
                  )}
                  {watchedValues.bullType === "Beef Bull" && (
                    <>
                      <TabsTrigger value="measurements" className="text-xs">
                        Measurements
                      </TabsTrigger>
                      <TabsTrigger value="genomic" className="text-xs">
                        Genomic
                      </TabsTrigger>
                    </>
                  )}
                  <TabsTrigger value="media" className="text-xs">
                    Media
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-6">
                  <Card className="animate-in slide-in-from-bottom-4 duration-500">
                    <CardHeader>
                      <CardTitle className="text-[#008c8fff] flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Basic Information
                      </CardTitle>
                      <CardDescription>
                        Essential animal identification and basic
                        characteristics
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <FormField
                        control={form.control}
                        name="dgr"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              DGR *
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(currentData, "dgr")}
                              newValue={watchedValues.dgr}
                              fieldName="dgr"
                            >
                              <FormControl>
                                <Input
                                  placeholder="Enter DGR"
                                  {...field}
                                  value={sanitizeInputValue(field.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              Name *
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(currentData, "name")}
                              newValue={watchedValues.name}
                              fieldName="name"
                            >
                              <FormControl>
                                <Input
                                  placeholder="Enter name"
                                  {...field}
                                  value={sanitizeInputValue(field.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="sex"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              Sex *
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(currentData, "sex")}
                              newValue={watchedValues.sex}
                              fieldName="sex"
                            >
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select sex" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                                  <SelectItem
                                    value="Male"
                                    className={
                                      "overflow-y-auto z-50 background-white"
                                    }
                                  >
                                    Male
                                  </SelectItem>
                                  <SelectItem
                                    value="Female"
                                    className={
                                      "overflow-y-auto z-50 background-white"
                                    }
                                  >
                                    Female
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="age"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              Age
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(currentData, "age")}
                              newValue={watchedValues.age}
                              fieldName="age"
                            >
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="Enter age"
                                  {...field}
                                  value={sanitizeInputValue(
                                    field.value,
                                    "number",
                                  )}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value === ""
                                        ? undefined
                                        : Number(e.target.value),
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="breed"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              Breed
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(currentData, "breed")}
                              newValue={watchedValues.breed}
                              fieldName="breed"
                            >
                              <FormControl>
                                <Input
                                  placeholder="Enter breed"
                                  {...field}
                                  value={sanitizeInputValue(field.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="bullType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              Bull Type *
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(currentData, "bullType")}
                              newValue={watchedValues.bullType}
                              fieldName="bullType"
                            >
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select bull type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                                  <SelectItem
                                    value="Beef Bull"
                                    className={
                                      "overflow-y-auto z-50 background-white"
                                    }
                                  >
                                    Beef Bull
                                  </SelectItem>
                                  <SelectItem
                                    value="Dairy Bull"
                                    className={
                                      "overflow-y-auto z-50 background-white"
                                    }
                                  >
                                    Dairy Bull
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="birthDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              Birth Date
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(currentData, "birthDate")}
                              newValue={watchedValues.birthDate}
                              fieldName="birthDate"
                            >
                              <FormControl>
                                <Input
                                  type="date"
                                  {...field}
                                  value={sanitizeInputValue(field.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="coatColor"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              Coat Color
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(currentData, "coatColor")}
                              newValue={watchedValues.coatColor}
                              fieldName="coatColor"
                            >
                              <FormControl>
                                <Input
                                  placeholder="Enter coat color"
                                  {...field}
                                  value={sanitizeInputValue(field.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="birthCondition"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={"text-[#008c8fff]"}>
                              Birth Condition
                            </FormLabel>
                            <FormFieldWrapper
                              currentValue={safeGet(
                                currentData,
                                "birthCondition",
                              )}
                              newValue={watchedValues.birthCondition}
                              fieldName="birthCondition"
                            >
                              <FormControl>
                                {/* Ensure we pass value as a prop, not children */}
                                <Textarea
                                  placeholder="Enter birth condition details"
                                  {...field}
                                  value={sanitizeInputValue(field.value)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormFieldWrapper>
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="physical" className="space-y-6">
                  <Card className="animate-in slide-in-from-bottom-4 duration-500">
                    <CardHeader>
                      <CardTitle>Physical Attributes</CardTitle>
                      <CardDescription>
                        Weight measurements and physical characteristics
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {[
                        {
                          name: "weight",
                          label: "Weight (kg)",
                          type: "number",
                        },
                        {
                          name: "birthWeight",
                          label: "Birth Weight (kg)",
                          type: "number",
                        },
                        {
                          name: "w120",
                          label: "120-day Weight",
                          type: "number",
                        },
                        { name: "ww", label: "Weaning Weight", type: "number" },
                        {
                          name: "pw",
                          label: "Post-weaning Weight",
                          type: "number",
                        },
                        {
                          name: "yw",
                          label: "Yearling Weight",
                          type: "number",
                        },
                        { name: "size", label: "Size", type: "text" },
                      ].map((field) => (
                        <FormField
                          key={field.name}
                          control={form.control}
                          name={field.name as keyof FormValues}
                          render={({ field: formField }) => (
                            <FormItem>
                              <FormLabel className={"text-[#008c8fff]"}>
                                {field.label}
                              </FormLabel>
                              <FormFieldWrapper
                                currentValue={safeGet(currentData, field.name)}
                                newValue={(watchedValues as any)[field.name]}
                                fieldName={field.name}
                              >
                                <FormControl>
                                  <Input
                                    type={field.type}
                                    placeholder={`Enter ${field.label.toLowerCase()}`}
                                    {...formField}
                                    value={sanitizeInputValue(
                                      formField.value,
                                      field.type,
                                    )}
                                    onChange={(e) => {
                                      const value =
                                        e.target.value === ""
                                          ? undefined
                                          : Number(e.target.value);
                                      formField.onChange(value);
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormFieldWrapper>
                            </FormItem>
                          )}
                        />
                      ))}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="genetic" className="space-y-6">
                  <Card className="animate-in slide-in-from-bottom-4 duration-500">
                    <CardHeader>
                      <CardTitle>Genetic Information</CardTitle>
                      <CardDescription>
                        Breeding and genetic data
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {[
                        { name: "paternalNucleus", label: "Paternal Nucleus" },
                        { name: "paternalType", label: "Paternal Type" },
                        { name: "breedingType", label: "Breeding Type" },
                        {
                          name: "racialComposition",
                          label: "Racial Composition",
                        },
                        { name: "betaLac", label: "Beta-lactoglobulin" },
                        { name: "kappaC", label: "Kappa-casein" },
                        { name: "betaC", label: "Beta-casein" },
                      ].map((field) => (
                        <FormField
                          key={field.name}
                          control={form.control}
                          name={field.name as keyof FormValues}
                          render={({ field: formField }) => (
                            <FormItem>
                              <FormLabel className={"text-[#008c8fff]"}>
                                {field.label}
                              </FormLabel>
                              <FormFieldWrapper
                                currentValue={safeGet(currentData, field.name)}
                                newValue={(watchedValues as any)[field.name]}
                                fieldName={field.name}
                              >
                                <FormControl>
                                  <Input
                                    placeholder={`Enter ${field.label.toLowerCase()}`}
                                    {...formField}
                                    value={sanitizeInputValue(formField.value)}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormFieldWrapper>
                            </FormItem>
                          )}
                        />
                      ))}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="parental" className="space-y-6">
                  {/* <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'> */}
                  <Card className="animate-in slide-in-from-left-4 duration-500">
                    <CardHeader>
                      <CardTitle>Dam Information</CardTitle>
                      <CardDescription>Mother's details</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ParentalInfoForm
                        damImageFile={damImageFile}
                        sireImageFile={sireImageFile}
                        setDamImageFile={setDamImageFile}
                        setSireImageFile={setSireImageFile}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="grandparental" className="space-y-6">
                  {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"> */}
                  <Card className="animate-in slide-in-from-bottom-4 duration-500">
                    <CardHeader>
                      <CardTitle>Paternal Grandparents</CardTitle>
                    </CardHeader>

                    <CardContent>
                      <GrandParentalInfoForm
                        // grand parents setters
                        setPaternalGrandfatherImageFile={
                          setPaternalGrandfatherImageFile
                        }
                        setPaternalGrandmotherImageFile={
                          setPaternalGrandmotherImageFile
                        }
                        setMaternalGrandfatherImageFile={
                          setMaternalGrandfatherImageFile
                        }
                        setMaternalGrandmotherImageFile={
                          setMaternalGrandmotherImageFile
                        }
                      />
                    </CardContent>
                  </Card>

                  {/* <Card className="animate-in slide-in-from-bottom-4 duration-500 delay-100"> */}
                  {/*   <CardHeader> */}
                  {/*     <CardTitle>Maternal Grandparents</CardTitle> */}
                  {/*   </CardHeader> */}
                  {/*   <CardContent className="space-y-4"> */}
                  {/*     {[ */}
                  {/*       { */}
                  {/*         name: "maternalGrandDamName", */}
                  {/*         label: "Maternal Grand Dam Name", */}
                  {/*       }, */}
                  {/*       { */}
                  {/*         name: "maternalGrandDamDGR", */}
                  {/*         label: "Maternal Grand Dam DGR", */}
                  {/*       }, */}
                  {/*       { */}
                  {/*         name: "maternalGrandSireName", */}
                  {/*         label: "Maternal Grand Sire Name", */}
                  {/*       }, */}
                  {/*       { */}
                  {/*         name: "maternalGrandSireDGR", */}
                  {/*         label: "Maternal Grand Sire DGR", */}
                  {/*       }, */}
                  {/*     ].map((field) => ( */}
                  {/*       <FormField */}
                  {/*         key={field.name} */}
                  {/*         control={form.control} */}
                  {/*         name={field.name as keyof FormValues} */}
                  {/*         render={({ field: formField }) => ( */}
                  {/*           <FormItem> */}
                  {/*             <FormLabel
											 className='text-[#008c8fff]>{field.label}</FormLabel> */}
                  {/*             <FormFieldWrapper */}
                  {/*               currentValue={safeGet( */}
                  {/*                 currentData, */}
                  {/*                 field.name, */}
                  {/*               )} */}
                  {/*               newValue={(watchedValues as any)[field.name]} */}
                  {/*               fieldName={field.name} */}
                  {/*             > */}
                  {/*               <FormControl> */}
                  {/*                 <Input */}
                  {/*                   placeholder={`Enter ${field.label.toLowerCase()}`} */}
                  {/*                   {...formField} */}
                  {/*                   value={sanitizeInputValue( */}
                  {/*                     formField.value, */}
                  {/*                   )} */}
                  {/*                 /> */}
                  {/*               </FormControl> */}
                  {/*               <FormMessage /> */}
                  {/*             </FormFieldWrapper> */}
                  {/*           </FormItem> */}
                  {/*         )} */}
                  {/*       /> */}
                  {/*     ))} */}
                  {/*   </CardContent> */}
                  {/* </Card> */}
                  {/* </div> */}
                </TabsContent>

                <TabsContent
                  value={"greatgrandparental"}
                  className={"space-y-6"}
                >
                  <GreatGrandparentalInfoForm
                    // Great-grandparent setters
                    setPaternalGrandSireGrandfatherImageFile={
                      setPaternalGrandSireGrandfatherImageFile
                    }
                    setPaternalGrandSireGrandmotherImageFile={
                      setPaternalGrandSireGrandmotherImageFile
                    }
                    setPaternalGrandDamGrandfatherImageFile={
                      setPaternalGrandDamGrandfatherImageFile
                    }
                    setPaternalGrandDamGrandmotherImageFile={
                      setPaternalGrandDamGrandmotherImageFile
                    }
                    setMaternalGrandSireGrandfatherImageFile={
                      setMaternalGrandSireGrandfatherImageFile
                    }
                    setMaternalGrandSireGrandmotherImageFile={
                      setMaternalGrandSireGrandmotherImageFile
                    }
                    setMaternalGrandDamGrandfatherImageFile={
                      setMaternalGrandDamGrandfatherImageFile
                    }
                    setMaternalGrandDamGrandmotherImageFile={
                      setMaternalGrandDamGrandmotherImageFile
                    }
                  />
                </TabsContent>

                {watchedValues.bullType === "Dairy Bull" && (
                  <TabsContent value="milk" className="space-y-6">
                    <Card className="animate-in slide-in-from-bottom-4 duration-500">
                      <CardHeader>
                        <CardTitle>Milk Properties</CardTitle>
                        <CardDescription>
                          Milk production and genetic markers
                        </CardDescription>
                      </CardHeader>
                      {/* <CardContent className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
														 <FormField
														 control={form.control}
														 name='milkAcc'
														 render={({ field }) => (
														 <FormItem>
														 <FormLabel className='text-[#008c8fff]>Milk Accuracy</FormLabel>
														 <FormFieldWrapper
														 currentValue={safeGet(currentData, "milkAcc")}
														 newValue={watchedValues.milkAcc}
														 fieldName='milkAcc'>
														 <FormControl>
														 <Input
														 type='number'
														 step='0.01'
														 min='0'
														 max='1'
														 placeholder='Enter milk accuracy (0-1)'
														 {...field}
														 value={sanitizeInputValue(field.value, "number")}
														 onChange={(e) =>
														 field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
														 }
														 />
														 </FormControl>
														 <FormMessage />
														 </FormFieldWrapper>
														 </FormItem>
														 )}
														 />

														 <FormField
														 control={form.control}
														 name='age1pAcc'
														 render={({ field }) => (
														 <FormItem>
														 <FormLabel className='text-[#008c8fff]>Age 1P
														 Accuracy</FormLabel>
														 <FormFieldWrapper
														 currentValue={safeGet(currentData, "age1pAcc")}
														 newValue={watchedValues.age1pAcc}
														 fieldName='age1pAcc'>
														 <FormControl>
														 <Input
														 type='number'
														 step='0.01'
														 min='0'
														 max='1'
														 placeholder='Enter age 1P accuracy (0-1)'
														 {...field}
														 value={sanitizeInputValue(field.value, "number")}
														 onChange={(e) =>
														 field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
														 }
														 />
														 </FormControl>
														 <FormMessage />
														 </FormFieldWrapper>
														 </FormItem>
														 )}
														 />

														 <FormField
														 control={form.control}
														 name='ebiAcc'
														 render={({ field }) => (
														 <FormItem>
														 <FormLabel className='text-[#008c8fff]>EBI Accuracy</FormLabel>
														 <FormFieldWrapper
														 currentValue={safeGet(currentData, "ebiAcc")}
														 newValue={watchedValues.ebiAcc}
														 fieldName='ebiAcc'>
														 <FormControl>
														 <Input
														 type='number'
														 step='0.01'
														 min='0'
														 max='1'
														 placeholder='Enter EBI accuracy (0-1)'
														 {...field}
														 value={sanitizeInputValue(field.value, "number")}
														 onChange={(e) =>
														 field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
														 }
														 />
														 </FormControl>
														 <FormMessage />
														 </FormFieldWrapper>
														 </FormItem>
														 )}
														 />

														 <FormField
														 control={form.control}
														 name='betaLac'
														 render={({ field }) => (
														 <FormItem>
														 <FormLabel
														 className='text-[#008c8fff]>Beta-lactoglobulin</FormLabel>
														 <FormFieldWrapper
														 currentValue={safeGet(currentData, "betaLac")}
														 newValue={watchedValues.betaLac}
														 fieldName='betaLac'>
														 <FormControl>
														 <Input
														 placeholder='Enter beta-lactoglobulin'
														 {...field}
														 value={sanitizeInputValue(field.value)}
														 />
														 </FormControl>
														 <FormMessage />
														 </FormFieldWrapper>
														 </FormItem>
														 )}
														 />

														 <FormField
														 control={form.control}
														 name='kappaC'
														 render={({ field }) => (
														 <FormItem>
														 <FormLabel className='text-[#008c8fff]>Kappa-casein</FormLabel>
														 <FormFieldWrapper
														 currentValue={safeGet(currentData, "kappaC")}
														 newValue={watchedValues.kappaC}
														 fieldName='kappaC'>
														 <FormControl>
														 <Input
														 placeholder='Enter kappa-casein'
														 {...field}
														 value={sanitizeInputValue(field.value)}
														 />
														 </FormControl>
														 <FormMessage />
														 </FormFieldWrapper>
														 </FormItem>
														 )}
														 />

														 <FormField
														 control={form.control}
														 name='betaC'
														 render={({ field }) => (
														 <FormItem>
														 <FormLabel className='text-[#008c8fff]>Beta-casein</FormLabel>
														 <FormFieldWrapper
														 currentValue={safeGet(currentData, "betaC")}
														 newValue={watchedValues.betaC}
														 fieldName='betaC'>
														 <FormControl>
														 <Input
														 placeholder='Enter beta-casein'
														 {...field}
														 value={sanitizeInputValue(field.value)}
														 />
														 </FormControl>
														 <FormMessage />
														 </FormFieldWrapper>
														 </FormItem>
														 )}
														 />
														 </CardContent> */}
                      <CardContent>
                        <MilkProperties />
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                {watchedValues.bullType === "Beef Bull" && (
                  <TabsContent value="measurements" className="space-y-6">
                    <Card className="animate-in slide-in-from-bottom-4 duration-500">
                      <CardHeader>
                        <CardTitle>Body Measurements</CardTitle>
                        <CardDescription>
                          Physical body measurements and dimensions
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                          {
                            name: "length_of_rump",
                            label: "Rump Length (cm)",
                          },
                          {
                            name: "height_of_rump",
                            label: "Rump Height (cm)",
                          },
                          { name: "lower_height", label: "Lower Height (cm)" },
                          { name: "back_height", label: "Back Height (cm)" },
                          { name: "body_length", label: "Body Length (cm)" },
                          { name: "rib_depth", label: "Rib Depth (cm)" },
                          {
                            name: "chest_perimeter",
                            label: "Chest Perimeter (cm)",
                          },
                          { name: "body_width", label: "Body Width (cm)" },
                          {
                            name: "scrotal_circumference",
                            label: "Scrotal Circumference (cm)",
                          },
                        ].map((field) => (
                          <FormField
                            key={field.name}
                            control={form.control}
                            name={field.name as keyof FormValues}
                            render={({ field: formField }) => (
                              <FormItem>
                                <FormLabel className={"text-[#008c8fff]"}>
                                  {field.label}
                                </FormLabel>
                                <FormFieldWrapper
                                  currentValue={safeGet(
                                    currentData,
                                    field.name,
                                  )}
                                  newValue={(watchedValues as any)[field.name]}
                                  fieldName={field.name}
                                >
                                  <FormControl>
                                    <Input
                                      type="number"
                                      // step="0.1"
                                      placeholder={`Enter ${field.label.toLowerCase()}`}
                                      {...formField}
                                      value={sanitizeInputValue(
                                        formField.value,
                                        "number",
                                      )}
                                      onChange={(e) =>
                                        formField.onChange(
                                          Number(e.target.value),
                                        )
                                      }
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormFieldWrapper>
                              </FormItem>
                            )}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                {watchedValues.bullType === "Beef Bull" && (
                  <TabsContent value="genomic" className="space-y-6">
                    <Card className="animate-in slide-in-from-bottom-4 duration-500">
                      <CardHeader>
                        <CardTitle>Genomic Data</CardTitle>
                        <CardDescription>
                          EPD, Accuracy, and DECA values for Beef production
                          traits
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-96">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                              { prefix: "mw_me", label: "Maternal Weaning" },
                              { prefix: "bw_de", label: "Birth Weight" },
                              { prefix: "ww_de", label: "Weaning Weight" },
                              { prefix: "aw_de", label: "Adult Weight" },
                              { prefix: "yw_de", label: "Yearling Weight" },
                              {
                                prefix: "sc365",
                                label: "Scrotal Circumference 365",
                              },
                              {
                                prefix: "sc450",
                                label: "Scrotal Circumference 450",
                              },
                              { prefix: "bs", label: "Body Score" },
                              { prefix: "prec", label: "Precocity" },
                              { prefix: "musc", label: "Muscularity" },
                              { prefix: "rea", label: "Ribeye Area" },
                              { prefix: "finishing", label: "Finishing" },
                              { prefix: "mar", label: "Marbling" },
                              { prefix: "iabcz", label: "IABCZ Index" },
                            ].map((group) => (
                              <div
                                key={group.prefix}
                                className="space-y-3 p-4 border rounded-lg"
                              >
                                <h4 className="font-medium text-sm">
                                  {group.label}
                                </h4>
                                {["epd", "acc", "deca"].map((suffix) => {
                                  const fieldName =
                                    `${group.prefix}_${suffix}` as keyof FormValues;
                                  return (
                                    <FormField
                                      key={fieldName}
                                      control={form.control}
                                      name={fieldName}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel className="text-xs">
                                            {suffix.toUpperCase()}
                                          </FormLabel>
                                          <FormFieldWrapper
                                            currentValue={safeGet(
                                              currentData,
                                              fieldName,
                                            )}
                                            newValue={
                                              (watchedValues as any)[fieldName]
                                            }
                                            fieldName={fieldName}
                                          >
                                            <FormControl>
                                              <Input
                                                type="number"
                                                // step="0.01"
                                                placeholder={`Enter ${suffix}`}
                                                {...field}
                                                value={sanitizeInputValue(
                                                  field.value,
                                                  "number",
                                                )}
                                                onChange={(e) =>
                                                  field.onChange(
                                                    Number(e.target.value),
                                                  )
                                                }
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormFieldWrapper>
                                        </FormItem>
                                      )}
                                    />
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}

                <TabsContent value="media" className="space-y-6">
                  <Card className="animate-in slide-in-from-bottom-4 duration-500">
                    <CardHeader>
                      <CardTitle>Media Gallery</CardTitle>
                      <CardDescription>
                        Images and videos of the animal
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <AnimalMediaInfo
                        images={img}
                        setImg={setImg}
                        setVideos={setVideos}
                        videos={videos}
                      />
                      <Separator className="my-6" />
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end space-x-4 pt-6 border-t">
                <Button variant="outline" type="button">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#015354ff]" disabled={form.formState.isSubmitting}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </form>
          </Form>
        )}
      </div>
    </div>
  );
}
